/**
 * Contains cookie related methods.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export default class CookieUtility {
  /**
   * Sets a cookie.
   *
   * @param {string} name Name of the cookie
   * @param {string} data Data of the cookie
   * @param {Date} [expires] When the cookie expires (defaults to null)
   * @param {string} [path] Path of the cookie (defaults to "/")
   * @param {string} [sameSite] SameSite attribute (defaults to "lax")
   * @returns {string} The complete cookie string
   */
  static set(
    name: string,
    data: string,
    expires: Date | null = null,
    path: string = '/',
    sameSite: string = 'lax'
  ): string {
    let cookie = name + '=' + data;
    if (expires !== null) {
      cookie += ';expires=' + expires.toUTCString();
    }
    cookie += `;path=${path};SameSite=${sameSite}`;
    document.cookie = cookie;
    return cookie;
  }

  /**
   * Gets a cookie.
   *
   * @param {string} name Name of the cookie to get
   * @returns {string|null} Cookie value, if existing
   */
  static get(name: string): string | null {
    if (!CookieUtility.exists(name)) {
      return null;
    }
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const parts = cookie.split('=');
      let cookieName = parts.shift();

      if (cookieName) {
        cookieName = cookieName.trim();
        const value = parts.join('=').trim();

        if (cookieName === name) {
          return value;
        }
      }
    }
    return null;
  }

  /**
   * Deletes a cookie.
   *
   * @param {string} name Name of the cookie to remove
   * @returns {boolean} True when the cookie was removed, otherwise false
   */
  static delete(name: string): boolean {
    if (!CookieUtility.exists(name)) {
      return false;
    }
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/';
    return true;
  }

  /**
   * Checks whether a cookie exists or not.
   *
   * @param {string} name Name of the cookie to check
   * @returns {boolean} True when the cookie exists, otherwise false
   */
  static exists(name: string): boolean {
    if (!name) {
      return false;
    }
    return document.cookie.indexOf(name + '=') >= 0;
  }
}
